import React from "react"
import { useCallback, useEffect, useMemo, useRef, useState, type Ref } from "react"
import OpLayoutEngineRenderer from "connected-components/op-layout-engine/renderer"
import { TEMPLATES } from "connected-components/op-layout-engine/templates"
import type { OpLayoutEngineRendererActions } from "connected-components/op-layout-engine/types"
import type { InstrumentWithMetadata } from "helpers/types/instruments/metadata"
import { useModules, isDefaultModulesSnapshot } from "../context/modules"
import { useDashboardOpLayoutEnginePersistence } from "../hooks/use-op-layout-engine-persistence"
import { useDashboardModuleActions } from "../hooks/use-dashboard-module-actions"
import { createModuleComponents } from "../helpers/module-renderers"
import { IDashboardContextValue } from "../context/types"

interface DashboardModulesRendererProps {
  TradeContext: React.Context<IDashboardContextValue>
  instrumentsWithMetadata: Record<string, InstrumentWithMetadata>
  hasMultipleInstruments?: boolean
  contentSets?: unknown[]
  modelMetadata?: unknown
  dockviewLayoutRef?: Ref<OpLayoutEngineRendererActions | null>
  onLayoutReady?: (actions: OpLayoutEngineRendererActions | null) => void
}

/**
 * Renders dashboard modules using OpLayoutEngine
 */
export default function DashboardModulesRenderer({
  TradeContext,
  instrumentsWithMetadata,
  hasMultipleInstruments,
  contentSets,
  modelMetadata,
  dockviewLayoutRef,
  onLayoutReady,
}: DashboardModulesRendererProps) {
  const {
    modules,
    isPreferencesLoaded: isModulesPreferencesLoaded,
    hasThematicBetasContentSet,
    removeModule,
    updateModule,
    addModule,
    resetModulesToDefault,
  } = useModules()
  const opLayoutEnginePersistence = useDashboardOpLayoutEnginePersistence(modules, isModulesPreferencesLoaded)
  const { handleRemoveModule, handleUpdateModuleTitle, handleDuplicateModule } = useDashboardModuleActions(modules, {
    removeModule,
    updateModule,
    addModule,
  })

  const layoutEngineActionsRef = useRef<OpLayoutEngineRendererActions | null>(null)
  const pendingResetApiRef = useRef<Parameters<OpLayoutEngineRendererActions["resetLayout"]>[0] | null>(null)
  const didRequestResetRef = useRef(false)
  const [layoutActionsVersion, setLayoutActionsVersion] = useState(0)

  useEffect(() => {
    if (!didRequestResetRef.current || !pendingResetApiRef.current) {
      return
    }
    if (!isDefaultModulesSnapshot(modules, hasThematicBetasContentSet)) {
      return
    }

    const runEngineReset = layoutEngineActionsRef.current?.resetLayout
    if (!runEngineReset) {
      return
    }

    const api = pendingResetApiRef.current
    runEngineReset(api)
    pendingResetApiRef.current = null
    didRequestResetRef.current = false
  }, [modules, hasThematicBetasContentSet, layoutActionsVersion])

  const wrappedOnLayoutReady = useCallback(
    (actions: OpLayoutEngineRendererActions | null) => {
      layoutEngineActionsRef.current = actions
      if (actions && didRequestResetRef.current) {
        setLayoutActionsVersion(version => version + 1)
      }

      if (!onLayoutReady) {
        return
      }
      if (!actions) {
        onLayoutReady(null)
        return
      }
      onLayoutReady({
        ...actions,
        resetLayout: (api) => {
          didRequestResetRef.current = true
          pendingResetApiRef.current = api
          resetModulesToDefault()
        },
      })
    },
    [onLayoutReady, resetModulesToDefault],
  )

  const components = useMemo(() => createModuleComponents(modules, {
    TradeContext,
    instrumentsWithMetadata,
    hasMultipleInstruments,
    contentSets,
    modelMetadata,
    onRemoveModule: handleRemoveModule,
    onUpdateModuleTitle: handleUpdateModuleTitle,
    onDuplicateModule: handleDuplicateModule,
    updateModule,
  }), [modules, TradeContext, contentSets, hasMultipleInstruments, instrumentsWithMetadata, modelMetadata, handleRemoveModule, handleUpdateModuleTitle, handleDuplicateModule, updateModule])

  return (
    <OpLayoutEngineRenderer
      layoutRef={dockviewLayoutRef ?? null}
      onLayoutReady={wrappedOnLayoutReady}
      components={components}
      template={TEMPLATES.HIERARCHICAL}
      persistence={opLayoutEnginePersistence}
      className="dashboard-op-layout"
    />
  )
}
